/**
 * ──────────────────────────────────────────────────────────────────────────
 * End-state goal (post-rebrand)
 * ──────────────────────────────────────────────────────────────────────────
 * This file is a *legacy* surface. The rebrand is steadily moving colour
 * consumption off `theme.ts` and onto Tailwind utility classes + CSS design
 * tokens. When Phase 7 (cleanup) and the post-rebrand LEGACY-consumer sweep
 * finish, `theme.ts` should shrink to roughly:
 *
 *   1. `ColorName` / `ColorObject` types — still needed for typing WP block
 *      attributes (the Gutenberg colour picker stores `{ name, color }`).
 *      Likely split into a `types/color.ts` file at that point.
 *
 *   2. `cviSemantic` — the JS escape hatch for inline styles / SVG `fill`
 *      props / library props that take a hex string. Primary colour
 *      consumption is via Tailwind utilities; `cviSemantic` only covers
 *      the "can't use a class here" cases. May move to its own file
 *      (e.g. `style/cvi-semantic.ts`).
 *
 *   3. Breakpoint values — `theme.breakpoints.TABLET` etc. used for JS-side
 *      viewport maths (`viewport.width <= theme.breakpoints.MOBILE`) that
 *      Tailwind's responsive modifiers can't express. Likely split into a
 *      `style/breakpoints.ts` file.
 *
 *   4. A minimal DS primitive palette — only the CVI DS-numeric primitives
 *      (`cviGrey-50..950`, `cviTurquoise-25..950`, `cviSand-25..950`) for the
 *      rare TSX consumer that genuinely needs a specific swatch hex in JS.
 *      LEGACY and INTERIM tiers are gone by this point.
 *
 * What gets deleted along the way:
 *   – the monolithic `theme` default export (colors.text / neutral / svg /
 *     footer groupings, nested theming data) — consumers migrate to utilities.
 *   – LEGACY entries in `palette` (Atos warm greys, pinks, beiges, atos*
 *     blues, one-off brand colours) — consumers migrate to utilities, then
 *     entries deleted. ~125 consumer files today.
 *   – INTERIM entries (`cviTurquoise`, `cviGrey01-04`, `cviSand01-02`, etc.)
 *     collapsed into the DS-numeric primitives in Phase 7.
 *   – Font-family string constants (`fontFamilyAtos*`) superseded by
 *     `--cvi-typography-family-*` tokens.
 *
 * Why keep this file alive at all instead of deleting it?
 *   Runtime colour lookups (WP-picked hexes flowing through `parseProps`,
 *   dynamic SVG fills, some library props) need a JS-accessible source of
 *   truth, and type exports have to live somewhere. Fighting to remove the
 *   last ~30 lines isn't a great return on effort vs. rebrand goals.
 *
 * Any addition to this file should earn its place against that endpoint —
 * if a new entry wouldn't survive the endpoint, prefer adding it as a CSS
 * token + Tailwind utility instead.
 */

export type ColorName =
    'transparent'
    | 'black'
    | 'white'
    // New color palette
    | 'blue400'
    | 'blue300'
    | 'blue200'
    | 'blue100'
    | 'grey700'
    | 'grey600'
    | 'grey500'
    | 'grey400'
    | 'grey300'
    | 'grey200'
    | 'grey100'
    // Semantic aliases
    | 'primary'
    | 'primaryHover'
    | 'link'
    | 'linkHover'
    // Legacy colors (for backward compatibility)
    | 'darkestGray'
    | 'darkGray'
    | 'gray'
    | 'lightGray'
    | 'mediumPink'
    | 'lightPink'
    | 'beige'
    | 'lightBeige'
    | 'verticalLineBlue'
    | 'green'
    | 'pink'
    | 'shopGreen'
    | 'paragraphColor'
    | 'atosDarkBlue'
    | 'atosBlue1'
    | 'atosBlue2'
    | 'atosBlue3'
    | 'atosLightBlue'
    | 'atosLightBlueRGBA50'
    | 'atosLightestBlue'
    // New CVI palette (2025)
    | 'cviTurquoise'
    | 'cviTurquoise04'
    | 'cviDeepBlue'
    | 'cviSand01'
    | 'cviSand02'
    | 'cviGrey01'
    | 'cviGrey02'
    | 'cviGrey03'
    | 'cviGrey04'
    | 'cviGreen'
    | 'cviOrange'
    | 'cviTeal'
    | 'cviCoral'
    // CVI DS-aligned primitives — full grey / turquoise / sand scales from the
    // DS Foundation Library, plus the Atos-extension turquoise/25.
    | 'cviGrey50'
    | 'cviGrey100'
    | 'cviGrey200'
    | 'cviGrey300'
    | 'cviGrey400'
    | 'cviGrey500'
    | 'cviGrey600'
    | 'cviGrey700'
    | 'cviGrey800'
    | 'cviGrey900'
    | 'cviGrey950'
    | 'cviTurquoise25'
    | 'cviTurquoise50'
    | 'cviTurquoise100'
    | 'cviTurquoise200'
    | 'cviTurquoise300'
    | 'cviTurquoise400'
    | 'cviTurquoise500'
    | 'cviTurquoise600'
    | 'cviTurquoise700'
    | 'cviTurquoise800'
    | 'cviTurquoise900'
    | 'cviTurquoise950'
    | 'cviSand25'
    | 'cviSand50'
    | 'cviSand100'
    | 'cviSand200'
    | 'cviSand300'
    | 'cviSand400'
    | 'cviSand500'
    | 'cviSand600'
    | 'cviSand700'
    | 'cviSand800'
    | 'cviSand900'
    | 'cviSand950'
    | 'componentFooterDefaultBgColor';
// Note: CVI semantic tokens (fg/bg/border/focus) are NOT listed here.
// They live in `cviSemantic` (escape-hatch for inline styles) and as Tailwind
// utilities (`text-cvi-fg-default`, `bg-cvi-bg-body`, etc. — the primary path
// for consumers). See the palette JSDoc below for the full rule.

export type ColorObject = {
    name: ColorName;
    color?: string;
}

export type HorizontalAlignment = 'left' | 'center' | 'right';
export enum VerticalAlignment {
    top = 'top',
    center = 'center',
    bottom = 'bottom',
    equalHeightColumns = 'stretch',
    spread = 'spread',
}

export type Spacing = 'auto' | 'none' | 'smallest' | 'smaller' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
export type GridSpacing = 'none' | 'small' | 'medium' | 'large';

export enum FlexAlignment {
    top = 'flex-start',
    bottom = 'flex-end',
    center = 'center',
    middle = 'center',
    left = 'flex-start',
    right = 'flex-end',
    stretch = 'stretch',
    spread = 'space-between',
}

export const fontFamilyAtosLight = '"Atos Light", -apple-system, BlinkMacSystemFont, "SF Pro", "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';
export const fontFamilyAtosRegular = '"Atos Regular", -apple-system, BlinkMacSystemFont, "SF Pro", "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';
export const fontFamilyAtosBold = '"Atos Bold", -apple-system, BlinkMacSystemFont, "SF Pro", "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"';

export type QueryTypeSlug = 'mobileMax' | 'tabletMax' | 'tabletMaxOriginal' | 'desktopMax' | 'desktopSmallMax' | 'desktopSmallMin' | 'desktopLargeMax' | 'desktopLargeMin' | 'desktopMin' | 'tabletMin' | 'mobileMin' | 'tabletSmallMax' | 'tabletSmallMin' | 'paddingMax' | 'paddingMin';
type QueryTypes = {
    [key in QueryTypeSlug ]: string;
}

export type TextTransform = 'none' | 'uppercase' | 'lowercase' | 'capitalize';

// Mirrors the CSS tokens in tailwind-theme-misc.css, which are authoritative —
// changing these alone will not move the layout.
const ROOT_FONT_SIZE = 11; // px — html/body font-size in tailwind.css
const MAX_WIDTH = 1320; // px — ds-container-max(2xl)
const MAX_WIDTH_REM = MAX_WIDTH / ROOT_FONT_SIZE;
const STANDARD_UNIT = 2.21; // rem — side gutter, outside MAX_WIDTH
const BLOCK_GRID_GAP = 24 / ROOT_FONT_SIZE; // rem — ds-space(8) desktop gutter
const GAP_SIZE = 3.14; // rem — legacy; now only slider spacing and --gapSize*
const COLUMNS = 12;

/**
 * Palette entries are grouped into three tiers — `CURRENT`, `INTERIM`, `LEGACY`.
 * `palette` holds primitive (hex-swatch) values. It is *not* the consumer
 * surface for semantic colours. `INTERIM` and `LEGACY` entries are kept alive
 * so already-authored content and un-migrated components keep rendering; they
 * get collapsed into the DS scale during Phase 7 cleanup.
 *
 * Consumer rule: Tailwind-first for colours.
 * ------------------------------------------
 * 1. **Primary path — Tailwind utility classes.** 99% of colour reads should
 *    be class-based: `class="text-cvi-fg-default bg-cvi-bg-body
 *    border-cvi-border-default"`. Every `--color-cvi-fg/bg/border/focus-*`
 *    token in `tailwind-theme-color.css` auto-generates a matching utility.
 *
 * 2. **Escape hatch — `cviSemantic`** (exported at the bottom of this file).
 *    Use only when a colour has to be passed as a JS value — inline
 *    `style={{…}}`, an SVG `fill` prop, a library that wants a colour string.
 *    Read by intent (`cviSemantic.fgLink`), never by primitive.
 *
 * 3. **`palette` is for primitive lookups.** Reach for a `cvi*` primitive
 *    from this object only when you explicitly want a specific swatch (for
 *    example `palette.cviGrey800` in a one-off decorative graphic), not when
 *    you want a semantic role. Semantic roles route through (1) or (2).
 *
 *   ✅ <p class="text-cvi-fg-default">                     // Tailwind utility
 *   ✅ <svg fill={cviSemantic.fgLink} />                   // escape-hatch alias
 *   ❌ <p style={{ color: palette.cviGrey800 }} />          // primitive-by-role
 *   ❌ palette.fgDefault                                     // no longer exists; use cviSemantic or utility
 */
export const palette = {
    // ======================================================================
    // BASELINE — primitives kept across every era (DS included).
    // ======================================================================
    transparent: 'transparent' as ColorName,
    black: '#000000' as ColorName,
    white: '#FFFFFF' as ColorName,

    // ======================================================================
    // LEGACY — Atos-era, kept for backwards compatibility.
    // Do NOT use in new code; prefer the CURRENT block below.
    // Collapsed into the DS scale in Phase 7.
    // ======================================================================

    // Atos "new color palette" (pre-rebrand — still Atos, not DS).
    blue400: '#6DADCA' as ColorName,
    blue300: '#A0CFE0' as ColorName,
    blue200: '#C0E2EB' as ColorName,
    blue100: '#E0F5F5' as ColorName,

    // Atos warm brown-grey scale (DS `cvi-grey-*` is a cool blue-grey scale, different family).
    grey700: '#373636' as ColorName,
    grey600: '#555151' as ColorName,
    grey500: '#736C6C' as ColorName,
    grey400: '#918787' as ColorName,
    grey300: '#B7B1B1' as ColorName,
    grey200: '#D1CDCD' as ColorName,
    grey100: '#EBE9E9' as ColorName,

    // Atos semantic aliases (legacy — CURRENT equivalent is fgDefault / fgMuted / etc. below).
    primary: '#555151' as ColorName,
    primaryHover: '#736C6C' as ColorName,
    link: '#00788A' as ColorName,
    linkHover: '#005C6A' as ColorName,

    // Atos one-off colours.
    darkestGray: '#403131' as ColorName,
    darkGray: '#726060' as ColorName,
    gray: '#918787' as ColorName,
    lightGray: '#BEB9B9' as ColorName,

    verticalLineBlue: '#A0CFE0' as ColorName, // alias for blue300

    mediumPink: '#d25960' as ColorName,
    lightPink: '#dd8186' as ColorName,
    lighterPink: '#F7DFE0' as ColorName,
    pink: '#d25960' as ColorName,

    beige: '#EEEEEE' as ColorName,
    lightBeige: '#F4F4F4' as ColorName,

    lightRed: '#F0414B' as ColorName,
    lightBlue: '#407EC9' as ColorName,
    darkGreen: '#074F59' as ColorName,
    darkBlue: '#1B365D' as ColorName,
    lightPurple: '#878CB4' as ColorName,
    darkColdGray: '#63666A' as ColorName,
    orange: '#F78E28' as ColorName,
    green: '#4B9560' as ColorName,
    shopGreen: '#DBE9D8' as ColorName,
    darkPurple: '#615E9B' as ColorName,
    lightColdGray: '#BEB9B9' as ColorName,
    brightGreen: '#76B640' as ColorName,

    // ======================================================================
    // INTERIM — an earlier CVI iteration (non-DS-numeric naming like `01`/`04`).
    // Several of these duplicate the DS-numeric primitives in CURRENT
    // (e.g. cviTurquoise === cviTurquoise500, cviGrey02 === cviGrey200).
    // Existing call sites still reference these; Phase 7 collapses them
    // into the CURRENT DS-numeric stops.
    // ======================================================================
    cviTurquoise:   '#00A2BA' as ColorName,
    cviTurquoise04: '#006A7A' as ColorName,
    cviDeepBlue:    '#213D46' as ColorName,
    cviSand01:      '#F1F0EC' as ColorName,
    cviSand02:      '#DCDAD5' as ColorName,
    cviGrey01:      '#F4F5F5' as ColorName,
    cviGrey02:      '#DDE2E4' as ColorName,
    cviGrey03:      '#CAD0D3' as ColorName,
    cviGrey04:      '#375059' as ColorName,
    cviGreen:       '#88BE75' as ColorName,
    cviOrange:      '#FBAB61' as ColorName,
    cviTeal:        '#378C87' as ColorName,
    cviCoral:       '#F17E64' as ColorName,

    // ======================================================================
    // CURRENT — Coloplast DS. Preferred for new code.
    // Full grey / turquoise / sand scales from the DS Foundation Library.
    // `cviSemantic` (below) references these entries for its aliases, so
    // remapping a primitive here propagates to every semantic consumer.
    // ======================================================================

    // Grey scale (11 stops).
    cviGrey50:  '#F2F3F3' as ColorName,
    cviGrey100: '#F2F3F3' as ColorName,                                    // ⚠️ MISNAMED — matches DS grey/50; rename in Phase 7. Consumed today by bg/neutral-subtle.
    cviGrey200: '#DDE2E4' as ColorName,                                    // border/muted
    cviGrey300: '#CAD0D3' as ColorName,                                    // border/default
    cviGrey400: '#B4BDC0' as ColorName,
    cviGrey500: '#7A8B90' as ColorName,
    cviGrey600: '#4D646B' as ColorName,                                    // fg/muted
    cviGrey700: '#3C545D' as ColorName,
    cviGrey800: '#213D46' as ColorName,                                    // fg/default + bg/dark-muted
    cviGrey900: '#1C333B' as ColorName,
    cviGrey950: '#15272D' as ColorName,                                    // same hex as componentFooterDefaultBgColor below.

    // Turquoise scale (11 DS stops + Atos extension /25).
    cviTurquoise25:  '#F6FBFD' as ColorName,                               // Atos-specific extension — DS scale starts at /50. Consumed by bg/block-brand.
    cviTurquoise50:  '#EAF7FA' as ColorName,
    cviTurquoise100: '#E0F5F9' as ColorName,
    cviTurquoise200: '#CCECF3' as ColorName,
    cviTurquoise300: '#B3E3ED' as ColorName,
    cviTurquoise400: '#4CBCD1' as ColorName,
    cviTurquoise500: '#00A2BA' as ColorName,                               // fg/brand
    cviTurquoise600: '#00788A' as ColorName,                               // fg/link
    cviTurquoise700: '#006A7A' as ColorName,                               // bg/interactive-strong
    cviTurquoise800: '#00515D' as ColorName,
    cviTurquoise900: '#003A44' as ColorName,
    cviTurquoise950: '#002E36' as ColorName,

    // Sand scale (12 DS stops, /25 included).
    cviSand25:  '#F9F9F7' as ColorName,                                    // bg/block-accent (DS `core/color/sand/25`)
    cviSand50:  '#F4F4F0' as ColorName,
    cviSand100: '#F1F0EC' as ColorName,                                    // duplicate of INTERIM cviSand01.
    cviSand200: '#E7E5DF' as ColorName,
    cviSand300: '#DCDAD5' as ColorName,                                    // duplicate of INTERIM cviSand02.
    cviSand400: '#D5D2CE' as ColorName,
    cviSand500: '#CAC8C4' as ColorName,
    cviSand600: '#858988' as ColorName,
    cviSand700: '#697072' as ColorName,
    cviSand800: '#485356' as ColorName,
    cviSand900: '#333F43' as ColorName,
    cviSand950: '#223035' as ColorName,

    componentFooterDefaultBgColor: '#15272D' as ColorName,                 // DS `component/footer/default/bg-color` — alias of cviGrey950.
    // Emerald base for bg/emerald-gradient-* reuses INTERIM `cviTeal` (#378C87).
    // CVI semantic aliases (fg/bg/border/focus) removed from `palette` —
    // they live as Tailwind utilities and in `cviSemantic` (see below).

    // ======================================================================
    // LEGACY (continued) — Atos-brand one-offs. Same guidance as above:
    // don't use in new code; kept for backwards compatibility only.
    // ======================================================================
    atosDarkBlue:         '#304b55' as ColorName,
    atosBlue1:            '#446f7d' as ColorName,
    atosBlue2:            '#5893a5' as ColorName,
    atosBlue3:            '#6dadca' as ColorName,
    atosLightBlue:        '#c0e2eb' as ColorName,
    atosLightBlueRGBA50:  'rgba(192, 226, 235, 0.7)' as ColorName,
    atosLightestBlue:     '#e0f5f5' as ColorName,

    paragraphColor: '#403131' as ColorName,
};

/**
 * CVI semantic tokens — each token names a PURPOSE (fgDefault, bgBody) rather
 * than a VALUE. Consumed as the JS escape hatch for inline styles / SVG fill
 * props (see palette JSDoc above). Mirror of `--color-cvi-fg-*|bg-*|border-*`
 * tokens in `tailwind-theme-color.css`.
 */
export const cviSemantic = {
    // Foreground
    fgDefault: palette.cviGrey800,
    fgDark: palette.cviGrey800,
    fgMuted: palette.cviGrey600,
    fgSubtle: palette.cviGrey600,
    fgBrand: palette.cviTurquoise500,
    fgLink: palette.cviTurquoise600,
    fgInverse: palette.white,
    fgLight: palette.white,
    fgOnColor: palette.white,
    fgOnColorMuted: '#FFFFFFCC' as ColorName,

    // Background
    bgDefault: palette.white,
    bgBody: palette.white,
    bgBlockDefault: palette.white,
    bgBlockBrand: palette.cviTurquoise25,
    bgBlockAccent: palette.cviSand25,                                  // #F9F9F7 — warm sand, distinct from block-brand
    bgDecorative2Subtle: palette.cviSand25,                            // alias for block-accent per DS
    bgDecorative3Subtle: palette.cviTurquoise25,
    bgNeutralSubtle: palette.cviGrey100,                               // #F2F3F3 — used by accordion/table surfaces
    bgDarkMuted: palette.cviGrey800,
    bgElevationRaised: palette.white,
    bgInteractiveStrong: palette.cviTurquoise700,
    bgEmeraldGradientStart: '#378C871A' as ColorName,                  // cviTeal @ 10%
    bgEmeraldGradientEnd: '#378C8700' as ColorName,                    // cviTeal @ 0%

    // Border
    borderDefault: palette.cviGrey300,
    borderMuted: palette.cviGrey200,
    borderInteractive: palette.cviTurquoise500,

    // Focus — ds-color(focus, default) = turquoise-600 at 90% alpha
    focusDefault: '#00788AE6' as ColorName,
};

/**
 * Pre-rebrand palette names that the editor's `hiddenFromPicker` set (in
 * `gutenberg-blocks/src/helpers/colors.js`) no longer exposes. Old posts
 * still carry these on `color.name` — neutralise them so brand-incompatible
 * hexes (e.g. `red` #F0414B / #c3353d, `paragraphColor` #403131) don't leak.
 */
const LEGACY_PALETTE_NAMES = new Set([
    'paragraphColor',
    'red', 'pink', 'mediumPink', 'lightPink',
    'darkGray', 'gray', 'lightGray',
    'beige', 'lightBeige',
]);

/**
 * Resolves a stored WP `{color, name}` object to a hex string for inline
 * styling. Legacy names → `cviSemantic.fgDefault`; any other named CVI colour
 * passes through as its stored hex (falling back to fgDefault if absent).
 * Returns undefined when the input has no usable colour, so callers can omit
 * the inline style and let the cascade supply `text-cvi-fg-default`.
 */
export const resolveLegacyTextColor = (
    colorObj: { color?: string; name?: string } | undefined | unknown[],
): string | undefined => {
    if (!colorObj || Array.isArray(colorObj) || !colorObj.name) return undefined;
    if (LEGACY_PALETTE_NAMES.has(colorObj.name)) return cviSemantic.fgDefault;
    return colorObj.color ?? cviSemantic.fgDefault;
};

export const breakpoints = {
    MOBILE: 480,
    TABLET_SMALL: 600,
    TABLET: 600,
    ORIGINAL_TABLET: 920,
    DESKTOP_LARGE: 1440,
    DESKTOP: 1270,
    DESKTOP_SMALL: 1024,
};

export const theme = {
    palette: palette,
    breakpoints: breakpoints,
    colors: {
        text: {
            paragraph: palette.darkestGray,
            heading: palette.darkGray,
            hero: palette.gray,
            quote: palette.darkGray,
            h1: palette.darkGray,
            h2: palette.darkGray,
            h3: palette.darkGray,
            h4: palette.darkGray,
            h5: palette.darkGray,
            h6: palette.darkGray,
            primary: palette.primary,
            pink: palette.pink,
            green: palette.green,
        },
        neutral: {
            darkOne: palette.darkGray,
            darkTwo: palette.darkGray,
            darkThree: palette.gray,
            lightOne: palette.lightGray,
            lightTwo: palette.beige,
            lightThree: palette.lightBeige,
            lightFour: palette.white,
            background: palette.beige,
        },
        svg: {
            default: palette.darkGray,
        },
        footer: {
            background: palette.darkGray,
            textColor: palette.white,
        },
    },
    borderRadius: {
        xsmall: '0.36rem',
        small: '1.07rem',
        medium: '1.43rem',
        large: '2.21rem',
    },
    headerMenu: {

    },
    fontSize: {
        smaller: '0.9rem',
        small: '1.3rem',
        normal: '1.6rem',
        large: '1.8rem',
    },
    header: {
        opaqueBackgroundColor: 'rgba(240, 239, 234, 0.5)',
        desktop: {
            height: {
                fixed: {
                    value: 10,
                    unit: 'rem',
                },
                floating: {
                    value: 6.43,
                    unit: 'rem',
                },
                fixedHcp: {
                    value: 7,
                    unit: 'rem',
                },
                floatingHcp: {
                    value: 10,
                    unit: 'rem',
                },
            },
        },
        mobile: {
            height: {
                value: 6.43,
                unit: 'rem',
            },
        },
    },
    sideBarNavigation: {
        mobile: {
            height: {
                value: 3.995,
                unit: 'rem',
            },
        },
    },
    verticalLineThickness: '2px',
    contentMaxWidthExtra: '1440px',
    contentMaxWidth: '1270px',
    grid: {
        columns: COLUMNS,
        standardUnit: STANDARD_UNIT, // rem
        gapSize: GAP_SIZE, // rem
        gapSizeTablet: GAP_SIZE * 1.5,
        gapSizeTabletSmall: GAP_SIZE * 0.75,
        gapSizeMobile: GAP_SIZE / 2,
        mobileColumnGap: 1.14,
        colSize: () => {
            const totalGapWidth = (COLUMNS - 1) * BLOCK_GRID_GAP;
            const widthSansGutters = MAX_WIDTH_REM - totalGapWidth;
            const remainingColWidth = widthSansGutters / COLUMNS;
            const roundedColWidth = Math.round(remainingColWidth * 100) / 100;            
            return roundedColWidth;
        },
    },
    maxWidth: MAX_WIDTH,
    formPadding: {
        desktop: '40px',
        mobile: '20px',
    },
    outerPadding: '3rem',
    gap: {
        col: '4rem',
        row: '3.75rem',
    },
    mobileGap: {
        col: '2rem',
        row: '2rem',
    },
    baseSize: 16,
    mediaQueries: {
        tabletSmallMin: `@media only screen and (min-width: ${breakpoints.TABLET_SMALL}px)`,
        tabletSmallMax: `@media only screen and (max-width: ${breakpoints.TABLET_SMALL}px)`,
        // Tablet and larger
        tabletMin: `@media only screen and (min-width: ${breakpoints.TABLET}px)`,
        // Tablet and smaller
        tabletMax: `@media only screen and (max-width: ${breakpoints.TABLET}px)`,
        // Original tabet size for reference and legacy
        tabletMaxOriginal: `@media only screen and (max-width: ${breakpoints.ORIGINAL_TABLET}px)`,
        // Only mobile
        mobileMin: `@media only screen and (min-width: ${breakpoints.MOBILE}px)`,
        mobileMax: `@media only screen and (max-width: ${breakpoints.MOBILE}px)`,
        // Desktop and larger
        desktopSmallMin: `@media only screen and (min-width: ${breakpoints.DESKTOP_SMALL}px)`,
        desktopSmallMax: `@media only screen and (max-width: ${breakpoints.DESKTOP_SMALL}px)`,
        desktopMin: `@media only screen and (min-width: ${breakpoints.DESKTOP}px)`,
        desktopMax: `@media only screen and (max-width: ${breakpoints.DESKTOP}px)`,
        desktopLargeMin: `@media only screen and (min-width: ${breakpoints.DESKTOP_LARGE}px)`,
        desktopLargeMax: `@media only screen and (max-width: ${breakpoints.DESKTOP_LARGE}px)`,
        paddingMax: `@media only screen and (max-width: ${breakpoints.DESKTOP}px)`,
        paddingMin: `@media only screen and (min-width: ${breakpoints.DESKTOP}px)`,
    } as QueryTypes,
    containerQueries: {
        tabletSmallMin: `@container (min-width: ${breakpoints.TABLET_SMALL}px)`,
        tabletSmallMax: `@container (max-width: ${breakpoints.TABLET_SMALL}px)`,
        // Tablet and larger
        tabletMin: `@container (min-width: ${breakpoints.TABLET}px)`,
        // Tablet size and smaller
        tabletMax: `@container (max-width: ${breakpoints.TABLET}px)`,
        // Original tabet size for reference and legacy
        tabletMaxOriginal: `@container (max-width: ${breakpoints.ORIGINAL_TABLET}px)`,
        // Only mobile
        mobileMin: `@container (min-width: ${breakpoints.MOBILE}px)`,
        // Only mobile
        mobileMax: `@container (max-width: ${breakpoints.MOBILE}px)`,
        // Desktop and larger
        desktopMin: `@container (min-width: ${breakpoints.DESKTOP}px)`,
        desktopMax: `@container (max-width: ${breakpoints.DESKTOP}px)`,
        desktopLargeMin: `@container (min-width: ${breakpoints.DESKTOP_LARGE}px)`,
        desktopLargeMax: `@container (max-width: ${breakpoints.DESKTOP_LARGE}px)`,
        paddingMax: `@container (max-width: ${breakpoints.DESKTOP}px)`,
        paddingMin: `@container (min-width: ${breakpoints.DESKTOP}px)`,
    } as QueryTypes,
    gridSpacing: { // The multiplier is pretty randomly selected, but it allows us to scale
        auto: 'initial',
        none: 0,
        smallest: STANDARD_UNIT * 0.25,
        smaller: STANDARD_UNIT * 0.5,
        small:  STANDARD_UNIT * 0.75,
        medium: STANDARD_UNIT,
        large: STANDARD_UNIT * 2,
        xlarge: STANDARD_UNIT * 3.5,
        xxlarge: STANDARD_UNIT * 5.5,
    },
    spacing: {
        xxxs: '0.5rem',
        xxs: '1rem',
        xs: '1.5rem',
        s: '2rem',
        ms: '2.5rem',
        m: '3rem',
        ml: '3.5rem',
        l: '4rem',
        xl: '4.5rem',
        xxl: '5rem',
        xxxl: '6rem',
    },
    scaling: {
        desktopLargeMax: 1,
        desktopMax: 0.5,
        tabletMax: 0.5,
        mobileMax: 0.35,
    },
    menuBar: {
        height: '80px',
    },
    lighten: (color: string, amount: number) => {
        let usePound = false;

        if (color[0] == '#') {
            color = color.slice(1);
            usePound = true;
        }

        const number = parseInt(color, 16);

        let red = (number >> 16) + amount;

        if (red > 255) {
            red = 255;
        } else if (red < 0) {
            red = 0;
        }

        let green = (number & 0x0000ff) + amount;

        if (green > 255) {
            green = 255;
        } else if (green < 0) {
            green = 0;
        }

        let blue = ((number >> 8) & 0x00ff) + amount;

        if (blue > 255) {
            blue = 255;
        } else if (blue < 0) {
            blue = 0;
        }

        return (usePound ? '#' : '') + (green | (blue << 8) | (red << 16)).toString(16);
    },
};

export default theme;
